import { Component } from "react";
import _ from "lodash";
import capitalize from "capitalize";
import moment from "moment";
import AiAssistant from "@shared/AiAssistant";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import Checkbox from "@shared/v2/Checkbox";
import DatePicker from "@shared/v2/DatePicker";
import Dropdown from "@shared/v2/Dropdown";
import FieldLabel from "@shared/v2/FieldLabel";
import IconButton from "@shared/v2/IconButton";
import Modal from "@shared/v2/Modal";
import RadioButton from "@shared/v2/RadioButton";
import TextInput from "@shared/v2/TextInput";
import Toggle from "@shared/v2/Toggle";
import { CircleInfoSolidV6, Plus, XmarkSolidV6 } from "@shared/v2/Icomoon";
import { DATE_FORMAT } from "@shared/v2/DateSelector/utils/constants";
import Tooltip from "@shared/v2/Tooltip";
import TextToggle from "@shared/v2/TextToggle";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import * as TaskHelpers from "./TaskIndexModalHelpers";
import OutcomeSelection from "./OutcomeSelection";
import TaskFor from "./TaskFor";
import { connect } from "react-redux";

let cachedAgentPromise = null;

const AGREEMENT_OUTCOMES = {
  "Signed - Buyer Agreement": "buyer",
  "Signed - Listing Agreement": "seller",
  "Signed - Both Buyer/Listing": "both",
};

const CONTINGENCY_OPTIONS = [
  { value: "after", label: "After" },
  { value: "before", label: "Before" },
];

const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const RECURRING_OPTIONS = [
  { value: "number", label: "Every _ Days" },
  { value: "weekly", label: "Weekly" },
];

const RECURRING_DOW_OPTIONS = [
  { value: 0, label: "On Sunday" },
  { value: 1, label: "On Monday" },
  { value: 2, label: "On Tuesday" },
  { value: 3, label: "On Wednesday" },
  { value: 4, label: "On Thursday" },
  { value: 5, label: "On Friday" },
  { value: 6, label: "On Saturday" },
];

export const defaultTask = {
  name: "",
  id: null,
  priority: 9,
  visibility: "",
  instructions: "",
  account_id: "",
  note: "",
  assigned_to_user_id: "",
  contingency_rule: {},
  contingency_event_and_type: [],
  taskable_id: "",
  taskable_type: "",
  taskable_name: "",
  appointment_type: "",
  outcome: "",
  location: "",
  externally_visible: false,
  contingent: false,
  recurring: false,
  showInstructions: false,
  showNotes: false,
  completed: null,
  user_id: "",
};

const getTaskButtonText = ({ task = {} }) => (task.id ? "Save" : "Create");
const getTaskTitle = ({ task = {} }) =>
  `${task.id ? "Edit" : "Add"} ${task.type === "Appointment" ? "Appointment" : "Task"} ${task.taskable_name ? `for ${task.taskable_name}` : ""}`;

class TaskForm extends Component {
  constructor(props) {
    super(props);
    const {
      accountUuid,
      isCreatingAppointment,
      task,
      task: {
        id,
        assigned_to_user_id: assignedToUserId,
        completed = false,
        contingent,
        instructions,
        location = "",
        note,
        outcome = "",
        priority,
        recurring,
        type,
        user_id: userId = "",
      } = {},
      taskable = {},
      taskFor = {},
      currentUser,
    } = props;
    defaultTask.appointment_type = taskable.appointment_type || null;
    defaultTask.taskable_id = props.taskFor?.uuid || taskable?.uuid || "";
    defaultTask.taskable_type = taskable.taskable_type || taskFor.object_type || "";
    defaultTask.taskable_name = taskable.task_name || taskFor.name || "";
    defaultTask.outcome = taskable.outcome || "";
    defaultTask.location = taskable.location || "";
    defaultTask.showInstructions = instructions;
    defaultTask.showNotes = note;
    defaultTask.user_id = userId || "";
    defaultTask.due_on = moment().format("YYYY-MM-DD");
    defaultTask.scheduled_time = moment().add(1, "hours").startOf("hour").unix();

    this.state = {
      task: {
        ...defaultTask,
        ...task,
        assigned_to_user_id: assignedToUserId || (id ? "anyone" : currentUser?.uuid),
        priority: priority || 9,
        account_id: accountUuid,
        outcome,
        location,
        completed,
        user_id: userId,
        type: isCreatingAppointment ? "Appointment" : type,
      },
      addTo: false,
      showRecurring: recurring,
      showContingency: contingent,
      showDue: !contingent,
      disabledButton: false,
    };

    this.updateTask = TaskHelpers.updateTask.bind(this);
    this.createTask = TaskHelpers.createTask.bind(this);
    this.handleTaskChange = TaskHelpers.handleTaskChange.bind(this);
    this.handleRecurrence = TaskHelpers.handleRecurrence.bind(this);
    this.handleMultiSelect = TaskHelpers.handleMultiSelect.bind(this);
    this.completeTask = TaskHelpers.completeTask.bind(this);
    this.uncompleteTask = TaskHelpers.uncompleteTask.bind(this);
    this.handleAssignedToUser = TaskHelpers.handleAssignedToUser.bind(this);
    this.tryCatchHandlr = TaskHelpers.tryCatchHandlr.bind(this);
    this.handleGoogleCalendar = TaskHelpers.handleGoogleCalendar.bind(this);
  }

  handleContingencyRuleChange = (e) => {
    const rule = { [e.target.name]: e.target.value };
    this.setState(({ task }) => ({
      task: { ...task, contingency_rule: { ...task.contingency_rule, ...rule } },
    }));
  };

  handleDueOnChange = (value) => {
    const momentValue = moment(value, "MM/DD/YYYY hh:mm A");
    this.setState({
      task: {
        ...this.state.task,
        due_on: momentValue.format("YYYY-MM-DD"),
        scheduled_time: momentValue.unix(),
      },
      // moment returns minutes, Date uses miliseconds
      disableCompletedCheckbox: momentValue.unix() * 1000 > Date.now(),
    });
  };

  handleAgreementCallback = () => {
    const { task } = this.state;
    const { agreementCallBack } = this.props;
    if (AGREEMENT_OUTCOMES[task.outcome]) {
      agreementCallBack?.(AGREEMENT_OUTCOMES[task.outcome]);
    }
  };

  handleSubmit = async () => {
    const { addTo, agents, task } = this.state;
    const { updateTask, listingUuid, currentUser, contactEmail, getCardTasks } = this.props;
    const taskData = { ...task };
    if (task.completed) taskData.completed_by_user_id = currentUser?.uuid;

    if (task.type === "Appointment" && !task.appointment_type) {
      this.setState({ errors: ["Appointment Type is required"] });
      return;
    }

    if (!task.taskable_id) {
      this.setState({ errors: ["Must select person, transaction or project"] });
      return;
    }

    if (task.id) {
      this.updateTask(taskData, listingUuid).then(() => {
        updateTask?.(taskData);
        this.handleAgreementCallback();
      });
    } else {
      this.createTask(
        taskData,
        listingUuid,
        agents,
        currentUser,
        contactEmail,
        addTo,
        this.handleAgreementCallback,
      ).then(() => {
        getCardTasks?.(task.taskable_id);
      });
    }
  };

  toggleCompleteTask = () =>
    this.setState(({ task }) => ({
      task: {
        ...task,
        completed: task.completed ? undefined : true,
        outcome: task.completed ? undefined : task.outcome,
      },
    }));

  toggleInstructions = () =>
    this.setState(({ task }) => ({
      task: { ...task, showInstructions: !task.showInstructions, instructions: null },
    }));

  toggleNotes = () =>
    this.setState(({ task }) => ({ task: { ...task, showNotes: !task.showNotes, note: null } }));

  toggleContingent = () =>
    this.setState(({ showContingency, showDue, task }) => ({
      showContingency: !showContingency,
      showDue: !showDue,
      task: {
        ...task,
        contingent: !showContingency,
        contingency_rule: showContingency
          ? {}
          : {
              days: 1,
              position: "after",
            },
      },
    }));

  toggleRecurring = () =>
    this.setState(({ showRecurring, task }) => ({
      showRecurring: !showRecurring,
      task: {
        ...task,
        recurring: !showRecurring,
        recurring_frequency: "number",
        recurring_every_so_many_days: 1,
      },
    }));

  taskableOptions = (taskableType) => {
    const actualType = /Listing/.test(taskableType) ? "Listing" : taskableType;
    const { task } = this.state;
    const taskOptions = this.taskableOptionsHash()[actualType];
    if (task.contingency_event_and_type.length == 0 || task.contingency_event_and_type[0] == "milestone") {
      return taskOptions;
    } else {
      const currentContingentEvent = [
        {
          value: task.contingency_event_and_type.join("-"),
          label: _.startCase(task.parent_task_name || task.contingency_event_and_type[1]),
        },
      ];
      return currentContingentEvent.concat(taskOptions);
    }
  };

  taskableOptionsHash = () => {
    const { accountMilestoneNames } = this.props;
    return {
      Person: this.milestonableMilestoneOptions(accountMilestoneNames.person),
      Listing: this.milestonableMilestoneOptions(accountMilestoneNames.listing),
      Project: this.milestonableMilestoneOptions(accountMilestoneNames.project),
    };
  };

  milestonableMilestoneOptions = (milestones) =>
    milestones.map((milestone) => ({
      value: `milestone-${milestone}`,
      label: milestone
        .split("_")
        .map((element) => capitalize(element))
        .join(" "),
    }));

  taskTypeOptions() {
    const taskTypes = [
      "To-Do",
      !this.props.taskable ? "Appointment" : null,
      "Call",
      "Text",
      "Email",
      "Mail",
      "Social Media",
      "Door Knock",
    ];
    return taskTypes.filter(Boolean).map((taskType) => ({ value: taskType, label: taskType }));
  }

  appointmentTypeOptions() {
    const realEstateTypes = [
      "Buyer Consultation",
      "Listing Consultation",
      "Buyer/Listing Consultation",
      "Showing Appointment",
      "Client Meeting",
      "General",
      "Follow Up",
    ].map((v) => ({ value: v, label: v }));
    const recruitingTypes = [
      "Meet & Greet",
      "Screening",
      "Recruiting Appointment",
      "Signing Appointment",
      "Recruiting Follow Up",
    ].map((v) => ({ value: v, label: v }));

    return [
      { label: "Real Estate", options: realEstateTypes },
      { label: "Recruiting", options: recruitingTypes },
    ];
  }

  taskForWatcher = (result) => {
    const taskableName = result.label?.split(" ").slice(0, 2).join(" ") || null;
    const taskableEmail = result.label?.split(" ").slice(2).join(" ") || null;
    this.setState(({ task }) => ({
      task: {
        ...task,
        taskable_type: result.object_type,
        taskable_id: result.uuid,
        taskable_name: taskableName,
        taskable_email: taskableEmail,
      },
    }));
  };

  taskForAppointment = () => {
    const { person } = this.props;
    this.setState((p) => ({
      task: {
        ...p.task,
        taskable_type: "Person",
        taskable_id: person.uuid || person.data.attributes.uuid,
        taskable_name: person.name
          ? person.name
          : `${person.first_name || person.data?.attributes?.first_name || ""} ${person.last_name || person.data?.attributes?.last_name || ""}`.trim() ||
            "No Name",
      },
    }));
  };

  handleUserId = () => {
    const { accountUuid, currentUser } = this.props;

    this.setState(({ task }) => ({
      task: {
        ...task,
        account_id: accountUuid,
        user_id: task.user_id || currentUser?.uuid,
        assigned_to_user_id: task.assigned_to_user_id || (task.id ? "anyone" : currentUser?.uuid),
      },
    }));
  };

  componentDidMount() {
    const { isCreatingAppointment } = this.props;
    const { task } = this.state;

    this.handleUserId();
    if (isCreatingAppointment) this.taskForAppointment();
    if (task.state === "completed") this.toggleCompleteTask();

    if (!cachedAgentPromise) {
      cachedAgentPromise = caseTransformingAxios.get("/simple_account_active_agents").then((response) =>
        [{ label: "Anyone", value: "anyone" }].concat(
          response.data.map((agent) => ({
            label: agent.fullName,
            value: agent.uuid,
            email: agent.email,
          })),
        ),
      );
    }

    cachedAgentPromise.then((agents) => {
      this.setState({ agents });
    });
  }

  render() {
    const {
      disabledButton,
      task,
      agents,
      addTo,
      showDue,
      showContingency,
      showRecurring,
      disableCompletedCheckbox,
      errors,
    } = this.state;

    const {
      showHeader,
      isCreatingAppointment,
      editTaskFor,
      handleTaskChange,
      taskDetails,
      taskFor,
      hideCancel,
      showTaskFor,
      onClose,
    } = this.props;

    const DATE_TIME_FORMAT = task.type === "Appointment" ? `${DATE_FORMAT} hh:mm A` : DATE_FORMAT;

    // CHECKME: editTaskFor is coming from TaskIndexModalHelpers.jsx where TaskForm is getting called for editing
    // CHECKME: isCreatingAppointment is coming from the PersonInteractions.jsx which called the  appointment tab on PDP page
    // CHECKME: createTask is coming from the NewTask.jsx which is called when + is click on Task idx
    return (
      <>
        {showHeader && <Modal.Header title={getTaskTitle({ task })} closeButton />}
        <Modal.Body className="tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden">
          <form className="task-form tw-flex-1 tw-flex tw-flex-col tw-gap-[24px] tw-pb-[24px] tw-overflow-auto">
            {errors && (
              <div className="task-errors">
                <Alert
                  variant="error"
                  text={
                    <dl className="tw-mb-0">
                      {errors.map((error, index) => (
                        <dd key={index}> {error} </dd>
                      ))}
                    </dl>
                  }
                />
              </div>
            )}

            <TextInput
              label="Task Details (max 255 characters)"
              isRequired
              name="name"
              onChange={this.handleTaskChange}
              value={task.name}
              maxLength={255}
              disabled={taskDetails ? true : false}
            />

            {(!task.showInstructions || !task.showNotes) && (
              <div className="tw-flex tw-gap-[12px]">
                {!task.showInstructions && (
                  <Button onClick={this.toggleInstructions}>
                    Add Instructions
                    <Plus size="s" className="tw-ml-[4px]" />
                  </Button>
                )}
                {!task.showNotes && (
                  <Button onClick={this.toggleNotes}>
                    Add Notes
                    <Plus size="s" className="tw-ml-[4px]" />
                  </Button>
                )}
              </div>
            )}

            {task.showInstructions && (
              <TextInput
                id="task-instructions-input"
                multiline
                size="auto"
                name="instructions"
                label={
                  <div className="tw-w-full tw-flex tw-justify-between tw-items-end tw-gap-[8px]">
                    <span>Instructions</span>
                    <div className="tw-flex tw-items-center tw-gap-[8px]">
                      <AiAssistant
                        textareaId="task-instructions-input"
                        messageType="Generic"
                        onInsertClick={(text) =>
                          handleTaskChange({ target: { name: "instructions", value: text } })
                        }
                      />
                      <IconButton size="small" schema="transparent" onClick={this.toggleInstructions}>
                        <XmarkSolidV6 />
                      </IconButton>
                    </div>
                  </div>
                }
                value={task.instructions || ""}
                onChange={this.handleTaskChange}
              />
            )}

            {task.showNotes && (
              <TextInput
                multiline
                size="auto"
                name="note"
                label={
                  <div className="tw-w-full tw-flex tw-justify-between tw-items-end tw-gap-[8px]">
                    <span>Notes</span>
                    <IconButton size="small" schema="transparent" onClick={this.toggleNotes}>
                      <XmarkSolidV6 />
                    </IconButton>
                  </div>
                }
                value={task.note || ""}
                onChange={this.handleTaskChange}
              />
            )}

            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <FieldLabel label="Priority (1: highest, 9: lowest)" isRequired />
              <div className="priority-buttons">
                {PRIORITY_OPTIONS.map((priorityNumber) => (
                  <RadioButton
                    key={priorityNumber}
                    labelClassName="after:!tw-content-none"
                    value={priorityNumber}
                    label={priorityNumber}
                    name="priority"
                    onClick={() => this.setState((p) => ({ task: { ...p.task, priority: priorityNumber } }))}
                    checked={task.priority === priorityNumber}
                  />
                ))}
              </div>
            </div>

            <div className="tw-flex tw-gap-[16px]">
              {!isCreatingAppointment && (
                <Dropdown
                  containerClassName="tw-flex-1"
                  label="Task Type"
                  isRequired
                  menuShouldComeToFront
                  options={this.taskTypeOptions()}
                  name="type"
                  onChange={(option, handler) =>
                    this.handleTaskChange({ target: { name: handler.name, value: option.value } })
                  }
                  value={
                    this.taskTypeOptions().find((opt) => opt.value === task.type) || this.taskTypeOptions()[0]
                  }
                  isDisabled={editTaskFor || taskDetails ? true : false}
                />
              )}

              {task.type === "Appointment" && (
                <Dropdown
                  containerClassName="tw-flex-1"
                  menuShouldComeToFront
                  label="Appointment Type"
                  isRequired
                  name="appointment_type"
                  onChange={(option) =>
                    this.setState((p) => ({ task: { ...p.task, appointment_type: option.value } }))
                  }
                  value={
                    this.appointmentTypeOptions()
                      .reduce((p, c) => [...p.options, ...c.options])
                      .find((opt) => opt.value === task.appointment_type) || null
                  }
                  options={this.appointmentTypeOptions()}
                  isDisabled={taskDetails ? true : false}
                />
              )}
            </div>

            <div className="tw-flex tw-gap-[16px]">
              {showTaskFor && !isCreatingAppointment && (
                <TaskFor id="task-for" onChange={this.taskForWatcher} taskFor={taskFor} />
              )}
              <Dropdown
                containerClassName="tw-flex-1"
                menuShouldComeToFront
                label="Assigned To"
                isSearchable
                name="assigned_to_user_id"
                onChange={(agent, handler) =>
                  this.handleTaskChange({ target: { name: handler?.name, value: agent?.value } })
                }
                isClearable={false}
                value={
                  agents?.find((agent) => agent.value === task.assigned_to_user_id) || agents?.[0] || null
                }
                options={agents}
                isDisabled={taskDetails ? true : false}
                isLoading={!agents?.length}
              />
            </div>

            {task.type !== "Appointment" && (
              <Checkbox
                label="Visible to Viewers"
                name="externally_visible"
                checked={task.externally_visible}
                onChange={this.handleTaskChange}
              />
            )}

            {task.type === "Appointment" && (
              <>
                <TextInput
                  label="Location"
                  onChange={(e) => {
                    const { value } = e.target;
                    this.setState((p) => ({ task: { ...p.task, location: value } }));
                  }}
                  defaultValue={task.location}
                  name="location"
                />
                {!task.completed && (
                  <Toggle
                    text="Add to Google Calendar"
                    onChange={() => this.setState((p) => ({ addTo: !p.addTo }))}
                    checked={addTo}
                  />
                )}
                <OutcomeSelection
                  task={task}
                  handleOutcomeOnChange={(outcome) =>
                    this.setState((p) => ({ task: { ...p.task, outcome } }))
                  }
                />
              </>
            )}

            <div className="tw-border-solid tw-border-[1px] tw-border-gray-5" />

            {task.type !== "Appointment" && (
              <div className="tw-flex tw-gap-[24px]">
                <Toggle text="Recurring" checked={showRecurring} onChange={this.toggleRecurring} />
                <Toggle
                  text={
                    <span className="tw-flex tw-items-center tw-gap-[4px]">
                      Contingent
                      <Tooltip
                        placement="top"
                        innerClassName="tw-max-w-[200px]"
                        trigger={<CircleInfoSolidV6 />}
                        content="Contingent tasks are due a specified amount of time before or after a specific event occurs"
                      />
                    </span>
                  }
                  checked={showContingency}
                  onChange={this.toggleContingent}
                />
              </div>
            )}

            {((showDue && !showContingency) || task.type === "Appointment") && (
              <DatePicker
                containerClassName="tw-flex-1"
                value={
                  task.type === "Appointment"
                    ? moment.unix(task.scheduled_time).format(DATE_TIME_FORMAT)
                    : moment(task.due_on, "YYYY-MM-DD").format(DATE_TIME_FORMAT)
                }
                enableTimePicker={task.type === "Appointment"}
                inputProps={{
                  id: "due_on",
                  disabled: taskDetails ? true : false,
                  isRequired: true,
                  label: `${taskDetails ? "Due Date" : "Date"} ${
                    task.type === "Appointment" ? "& Time" : ""
                  }`,
                }}
                onChange={this.handleDueOnChange}
                position="top"
              />
            )}

            {task.type !== "Appointment" && (showContingency || showRecurring) && (
              <>
                {showContingency && (
                  <div className="tw-flex tw-gap-[16px]">
                    <div className="tw-flex-1 tw-flex tw-gap-[16px]">
                      <TextInput
                        isRequired
                        containerClassName="tw-flex-1"
                        size="1"
                        label="# of days"
                        name="days"
                        onChange={this.handleContingencyRuleChange}
                        value={task.contingency_rule.days || ""}
                      />
                      <div className="tw-flex-1 tw-flex tw-items-end">
                        <TextToggle
                          options={CONTINGENCY_OPTIONS}
                          value={task.contingency_rule.position}
                          onChange={(e) =>
                            this.handleContingencyRuleChange({
                              target: { name: "position", value: e.target.value },
                            })
                          }
                        />
                      </div>
                    </div>
                    <Dropdown
                      isRequired
                      menuShouldComeToFront
                      containerClassName="tw-flex-1"
                      label="Event"
                      name="event"
                      onChange={(option, handler) =>
                        this.handleContingencyRuleChange({
                          target: { name: handler.name, value: option.value },
                        })
                      }
                      value={this.taskableOptions(task.taskable_type)?.find(
                        (opt) => opt.value === task.contingency_rule.event,
                      )}
                      options={this.taskableOptions(task.taskable_type)}
                    />
                  </div>
                )}
                <div className="tw-flex tw-gap-[16px]">
                  {showRecurring && (
                    <div className="tw-flex-1 tw-flex tw-gap-[16px]">
                      <Dropdown
                        menuShouldComeToFront
                        containerClassName="tw-flex-1"
                        label="Recurring"
                        name="recurring_frequency"
                        onChange={(option, handler) =>
                          this.handleTaskChange({ target: { name: handler.name, value: option.value } })
                        }
                        value={RECURRING_OPTIONS.find((opt) => opt.value === task.recurring_frequency)}
                        options={RECURRING_OPTIONS}
                      />
                      {task.recurring_frequency == "number" && (
                        <TextInput
                          containerClassName="tw-flex-1"
                          isRequired
                          size="1"
                          label="# of days"
                          onChange={this.handleTaskChange}
                          name="recurring_every_so_many_days"
                          value={task.recurring_every_so_many_days || ""}
                        />
                      )}
                      {task.recurring_frequency == "weekly" && (
                        <Dropdown
                          menuShouldComeToFront
                          containerClassName="tw-flex-1"
                          label="Day of Week"
                          isSearchable
                          isClearable={false}
                          name="recurring_day_of_week"
                          onChange={(option, handler) =>
                            this.handleTaskChange({ target: { name: handler.name, value: option.value } })
                          }
                          value={RECURRING_DOW_OPTIONS.find(
                            (opt) => opt.value === task.recurring_day_of_week,
                          )}
                          options={RECURRING_DOW_OPTIONS}
                        />
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </form>
        </Modal.Body>

        <Modal.Footer className="tw-flex tw-justify-between tw-items-center">
          {hideCancel ? (
            <span />
          ) : (
            <Button data-dismiss="modal" onClick={onClose} size="medium" schema="tertiary">
              Cancel
            </Button>
          )}

          <div className="tw-flex tw-gap-[8px]">
            <Checkbox
              checked={task.completed}
              label="Mark As Complete"
              onClick={this.toggleCompleteTask}
              disabled={disableCompletedCheckbox || task.state === "completed"}
            />
            {/* btn will be disabled when its creating or scheduling appointment from PDP/ contact index page ,
            task name, priority, task type, appointment type and dueon date is required feild for appointment*/}
            <Button
              isLoading={disabledButton}
              disabled={
                !task.name.length ||
                (task.type === "Appointment" &&
                  (!task.appointment_type || !task.due_on || (task.completed && !task.outcome)))
              }
              size="medium"
              onClick={this.handleSubmit}
            >
              {getTaskButtonText({ task })}
            </Button>
          </div>
        </Modal.Footer>
      </>
    );
  }
}

TaskForm.defaultProps = {
  isCreatingAppointment: false,
  taskDetails: false,
};

export default connect(({ layout }) => ({
  accountUuid: layout.currentUser.account.uuid,
  accountMilestoneNames: layout.sidebar.accountMilestoneNames,
  currentUser: layout.currentUser,
}))(TaskForm);
